import type { FC, ReactNode } from 'react'
import type { JobPhotoRow } from '@/lib/cms'

import Link from 'next/link'
import Image from 'next/image'
import { ArrowRight, MapPin } from 'lucide-react'
import { revealDelay } from '@/lib/motion'

interface JobPhotoGridProps {
    photos: JobPhotoRow[]
    title?: string
    intro?: string
    showLink?: boolean
    id?: string
}

const JobPhotoGrid: FC<JobPhotoGridProps> = ({ photos = [], title = "Photos from our jobs", intro, showLink = true, id = 'job-photos' }): ReactNode => {
    if (!photos.length) return null

    return (
        <section aria-labelledby={`${id}-heading`} className='mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8'>
            <div className='flex flex-wrap items-end justify-between gap-6' data-reveal>
                <div className='max-w-2xl'>
                    <h2 id={`${id}-heading`} className='text-3xl font-bold sm:text-4xl'>
                        {title}
                    </h2>

                    {intro && (
                        <p className='mt-3 text-lg text-muted-foreground'>
                            {intro}
                        </p>
                    )}
                </div>

                {showLink && (
                    <Link href='/projects/' className='inline-flex items-center gap-2 text-lg font-bold text-primary underline underline-offset-4'>
                        See more of our work
                        <ArrowRight className='size-5' aria-hidden='true' />
                    </Link>
                )}
            </div>

            <ul className='mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3'>
                {photos.map((photo, index) => (
                    <li key={photo.id} data-reveal style={revealDelay((index % 3) * 100)}>
                        <figure className='overflow-hidden rounded-3xl bg-white shadow-sm'>
                            <div className='group relative aspect-[3/4] overflow-hidden bg-muted'>
                                {photo.photo && (
                                    <Image
                                        src={photo.photo}
                                        alt={photo.photo_alt ?? photo.title}
                                        fill
                                        sizes='(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw'
                                        className='reveal-settle object-cover transition-transform duration-700 group-hover:scale-105'
                                    />
                                )}

                                <span className='absolute left-3 top-3 rounded-md bg-navy-deep/85 px-2.5 py-1 text-sm font-semibold text-white'>
                                    ACM job photo
                                </span>
                            </div>

                            <figcaption className='p-5'>
                                <p className='text-xl font-bold text-navy' data-mdlfy-cms={`job_photos:${photo.id}:title`}>{photo.title}</p>

                                {photo.town && (
                                    <p className='mt-1 flex items-center gap-1.5 text-base text-muted-foreground'>
                                        <MapPin className='size-4' aria-hidden='true' />
                                        <span data-mdlfy-cms={`job_photos:${photo.id}:town`}>{photo.town}</span>
                                    </p>
                                )}

                                {photo.details && (
                                    <p className='mt-2 text-base' data-mdlfy-cms={`job_photos:${photo.id}:details`}>{photo.details}</p>
                                )}
                            </figcaption>
                        </figure>
                    </li>
                ))}
            </ul>
        </section>
    )
}

export default JobPhotoGrid
